import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import {
  Box,
  CircularProgress,
  Typography,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import {
  useDataProvider,
  DatePicker,
  TimePicker,
  TextArea,
  useUserAccess,
  FieldWrapperVertical,
  SnackbarApiErrorMessage,
  useLocalization,
  useDialog,
  NumericTextField,
  useSnackbar,
} from '@ui-framework/index';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import { nanoid } from 'nanoid';
import DialogCloseButton from './DialogCloseButton';
import IconLibrary from '../IconLibrary';

function ConditionallyAcceptJobDialog({ open, onClose, jobInfo }) {
  const userAccess = useUserAccess();
  const { formatMessage } = useLocalization();
  const snackbar = useSnackbar();
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { getApiUrl, put } = useDataProvider(provider);
  const userID = userAccess?.company?.id;
  const { confirmationRef } = useDialog();
  const { serviceJobNumber = '' } = jobInfo;

  const [loading, setLoading] = useState(false);
  const validationSchema = yup.object().shape({
    differentDay: yup.boolean(),
    moreMoney: yup.boolean(),
    other: yup.boolean(),
    checkboxGroup: yup.mixed().test(
      'at-least-one-checked',
      formatMessage({
        id: 'AT_LEAST_ONE_CONDITION_REQUIRED',
        defaultMessage: 'At least one condition must be selected',
      }),
      (_, context) => {
        const { differentDay, moreMoney, other } = context.parent;
        return !!(differentDay || moreMoney || other);
      },
    ),
    appointmentDate: yup.string().when('differentDay', {
      is: true,
      then: schema =>
        schema
          .required(
            formatMessage({
              id: 'APPOINTMENT_DATE_REQUIRED',
              defaultMessage: 'Date is required',
            }),
          )
          .test(
            'is-date-in-past',
            formatMessage({
              id: 'APPOINTMENT_DATE_PAST',
              defaultMessage: 'Date cannot be in the past',
            }),
            value => {
              return !dayjs(value).isBefore(dayjs(), 'day');
            },
          ),
      otherwise: schema => schema.notRequired(),
    }),
    additionalFee: yup.string().when('moreMoney', {
      is: true,
      then: schema =>
        schema
          .required(
            formatMessage({
              id: 'ADDITIONAL_FEE_REQUIRED',
              defaultMessage: 'Additional fee is required',
            }),
          )
          .test(
            'is-greater-than-one',
            formatMessage({
              id: 'ADDITIONAL_FEE_MIN',
              defaultMessage: 'Additional fee must be greater than 1',
            }),
            value => parseFloat(value) >= 1,
          ),
      otherwise: schema => schema.notRequired(),
    }),
    additionalConditionDescription: yup.string().when('other', {
      is: true,
      then: schema =>
        schema.required(
          formatMessage({
            id: 'CONDITIONAL_DESCRIPTION_REQUIRED',
            defaultMessage: 'Additional Condition Description is required',
          }),
        ),
      otherwise: schema => schema.notRequired(),
    }),
  });

  const methods = useForm({
    defaultValues: {
      differentDay: false,
      moreMoney: false,
      other: false,
    },
    resolver: yupResolver(validationSchema),
  });
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    watch,
    trigger,
    clearErrors,
  } = methods;

  const buttons = [
    {
      children: formatMessage({
        id: 'CONDITIONALLY ACCEPT',
        defaultMessage: 'Conditionally Accept',
      }),
      type: 'Submit',
      variant: 'contained',
      color: 'primary',
      startIcon: <IconLibrary.Accept />,
    },
    {
      children: formatMessage({ id: 'CANCEL', defaultMessage: 'Cancel' }),
      onClick: () => {
        onClose(null);
      },
      variant: 'contained',
      color: 'secondary',
      startIcon: <IconLibrary.Cancel />,
    },
  ];

  const [differentDay, moreMoney, other] = watch([
    'differentDay',
    'moreMoney',
    'other',
  ]);

  const errorByType = Object.values(errors).find(
    err => err?.type === 'at-least-one-checked',
  );

  useEffect(() => {
    if (open && jobInfo?.dispatchEvent?.id) {
      const dispatch = jobInfo?.dispatchEvent;
      methods.reset({
        differentDay: !!dispatch?.serviceDate,
        appointmentDate: dispatch?.serviceDate || '',
        serviceDateAfterHour: dispatch?.serviceDateAfterHour || '',
        serviceDateBeforeHour: dispatch?.serviceDateBeforeHour || '',
        moreMoney: !!dispatch?.additionalMoney,
        additionalFee: dispatch?.additionalMoney || '',
        other:
          dispatch?.status === 'ACCEPTED_WITH_CONDITIONS'
            ? !!dispatch?.reason
            : false,
        additionalConditionDescription:
          dispatch?.status === 'ACCEPTED_WITH_CONDITIONS'
            ? dispatch?.reason || ''
            : '',
      });
    } else if (open) {
      methods.reset();
    }
  }, [open, jobInfo]);

  useEffect(() => {
    if (!differentDay) {
      methods.setValue('appointmentDate');
      methods.setValue('serviceDateAfterHour');
      methods.setValue('serviceDateBeforeHour');
    }
    if (!moreMoney) {
      methods.setValue('additionalFee');
    }
    if (!other) {
      methods.setValue('additionalConditionDescription');
    }
  }, [differentDay, moreMoney, other]);

  async function onFormSubmit(data) {
    setLoading(true);
    const dispatchEventValue = jobInfo?.dispatchEvent;
    if (!dispatchEventValue?.id) {
      snackbar.showMessage({
        type: 'error',
        data: formatMessage({
          id: 'ACCEPT_CONDITIONALLY_FAILURE',
          defaultMessage: 'Error: Could not conditionally accept the job',
        }),
      });
    } else {
      const body = {
        dispatchEventId: dispatchEventValue?.id,
        serviceDate: data.appointmentDate || null,
        additionalMoney: data.additionalFee || null,
        serviceDateAfterHour: data.serviceDateAfterHour || null,
        serviceDateBeforeHour: data.serviceDateBeforeHour || null,
        reason: data.additionalConditionDescription || null,
      };

      const response = await put(
        `${getApiUrl()}/sxcl/v1/service-providers/${userID}/pending-acceptance/${
          jobInfo?.id
        }/accept-conditionally`,
        body,
      );
      if (response.ok) {
        confirmationRef.current.init({
          message: 'Job was conditionally accepted successfully',
          positiveButtonText: 'Close',
          width: 360,
          title: 'Job Conditionally Accepted',
          onChange: () => {
            confirmationRef.current.setOpen(false);
            onClose(nanoid());
          },
        });
        confirmationRef.current.setOpen(true);
      } else {
        snackbar.showMessage({
          type: 'error',
          data: <SnackbarApiErrorMessage errors={response?.errors} />,
        });
      }
    }
    setLoading(false);
  }

  return (
    <Dialog
      open={open}
      onClose={() => {
        onClose(null);
      }}
      maxWidth="sm"
      fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconLibrary.ConditionllyAcceptServiceJob />
        {formatMessage({
          id: 'CONDITIONALLY_ACCEPT_JOB',
          defaultMessage: 'Conditionally Accept Job',
        })}
        &nbsp;&ndash;
        <Typography color="primary" variant="body">
          {serviceJobNumber}
        </Typography>
        <DialogCloseButton
          disabled={loading}
          onClose={() => {
            onClose(null);
          }}
        />
      </DialogTitle>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <DialogContent>
            {loading ? (
              <Box textAlign="center">
                <CircularProgress size="30px" />
                <Typography>Processing</Typography>
              </Box>
            ) : (
              <>
                {errorByType && (
                  <Typography color="error" sx={{ mb: 2 }}>
                    {errorByType.message}
                  </Typography>
                )}

                <input type="hidden" {...register('checkboxGroup')} />

                <Grid2 container spacing={1}>
                  <Controller
                    name="differentDay"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            {...field}
                            checked={field.value}
                            onChange={e => {
                              field.onChange(e.target.checked);
                              if (!e.target.checked) {
                                clearErrors([
                                  'appointmentDate',
                                  'serviceDateAfterHour',
                                  'serviceDateBeforeHour',
                                ]);
                              }
                              trigger('checkboxGroup');
                            }}
                          />
                        }
                        label="Different Day"
                      />
                    )}
                  />
                  <Grid2 container spacing={1}>
                    <Grid2 xs={5}>
                      <FieldWrapperVertical
                        field={{
                          name: 'appointmentDate',
                          label: {
                            localeId: 'SERVICE_DATE_LABEL',
                            defaultMessage: 'Service Date',
                          },
                        }}
                        errors={errors}>
                        <DatePicker
                          name="appointmentDate"
                          disabled={!differentDay}
                          disablePast
                        />
                      </FieldWrapperVertical>
                    </Grid2>
                    <Grid2 xs={3}>
                      <Controller
                        name="serviceDateAfterHour"
                        control={control}
                        render={({ field }) => (
                          <FieldWrapperVertical
                            field={{
                              name: 'serviceDateAfterHour',
                              label: {
                                localeId: 'SERVICE_DATE_AFTER_HOUR_LABEL',
                                defaultMessage: 'After',
                              },
                            }}>
                            <TimePicker {...field} disabled={!differentDay} />
                          </FieldWrapperVertical>
                        )}
                      />
                    </Grid2>
                    <Grid2 xs={3}>
                      <Controller
                        name="serviceDateBeforeHour"
                        control={control}
                        render={({ field }) => (
                          <FieldWrapperVertical
                            field={{
                              name: 'serviceDateBeforeHour',
                              label: {
                                localeId: 'SERVICE_DATE_BEFORE_HOUR_LABEL',
                                defaultMessage: 'Before',
                              },
                            }}>
                            <TimePicker {...field} disabled={!differentDay} />
                          </FieldWrapperVertical>
                        )}
                      />
                    </Grid2>
                  </Grid2>
                  <Grid2 xs={5}>
                    <Controller
                      name="moreMoney"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              {...field}
                              checked={field.value}
                              onChange={e => {
                                field.onChange(e.target.checked);
                                if (!e.target.checked) {
                                  clearErrors(['additionalFee']);
                                }
                                trigger('checkboxGroup');
                              }}
                            />
                          }
                          label="More Money"
                        />
                      )}
                    />
                    <FieldWrapperVertical
                      field={{
                        name: 'additionalFee',
                        label: {
                          localeId: 'ADDITIONAL_FEE_LABEL',
                          defaultMessage: 'Additional Fee',
                        },
                      }}
                      errors={errors}>
                      <NumericTextField
                        {...register('additionalFee')}
                        fixedDecimalScale={2}
                        positiveNumber
                        startAdornment="$"
                        disabled={!moreMoney}
                        error={!!errors?.additionalFee}
                        helperText={errors?.additionalFee?.message}
                      />
                    </FieldWrapperVertical>
                  </Grid2>
                  <Grid2 xs={12}>
                    <Controller
                      name="other"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              {...field}
                              checked={field.value}
                              onChange={e => {
                                field.onChange(e.target.checked);
                                if (!e.target.checked) {
                                  clearErrors([
                                    'additionalConditionDescription',
                                  ]);
                                }
                                trigger('checkboxGroup');
                              }}
                            />
                          }
                          label="Other"
                        />
                      )}
                    />
                    <FieldWrapperVertical
                      field={{
                        name: 'additionalConditionDescription',
                        label: {
                          localeId: 'ADDITIONAL_CONDITOIN_DESCRIPTION_LABEL',
                          defaultMessage: 'Additional Condition Description',
                        },
                      }}
                      errors={errors}>
                      <TextArea
                        {...register('additionalConditionDescription')}
                        disabled={!other}
                        error={!!errors?.additionalConditionDescription}
                        helperText={
                          errors?.additionalConditionDescription?.message
                        }
                      />
                    </FieldWrapperVertical>
                  </Grid2>
                </Grid2>
              </>
            )}
          </DialogContent>
          <DialogActions
            display="flex"
            sx={{ justifyContent: 'center', my: 2 }}>
            {buttons.map(btn => {
              const { children, ...rest } = btn;
              return (
                <Button key={children} disabled={loading} {...rest}>
                  {children}
                </Button>
              );
            })}
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
}

export default ConditionallyAcceptJobDialog;
